/* PitchTutorial — "How this works" explainer modal for first-time pitch deck
   builders. Opens automatically once per device (localStorage key), reopenable
   from the editor header. Skim-able, dismissable, gentle nudge to the advisor. */
import React, { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight, BookOpen, Lightbulb, MagicWand,
  PencilSimpleLine, Image as ImageIcon, Shield, PaperPlaneTilt, Sparkle,
  PaintBrushBroad, StackSimple, ChatCircleText } from "@phosphor-icons/react";

const C = {
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  panel: "#0A2826",
  bgDark: "#000",
};

// LocalStorage key is deck-scoped so the tutorial fires once per new deck
// (not once per device). Founders juggling multiple pitches see the
// "How this works" refresher for each brand-new deck they start.
const LS_KEY_PREFIX = "clinq_pitch_tutorial_seen_v1";
const lsKey = (deckId) => (deckId ? `${LS_KEY_PREFIX}:${deckId}` : LS_KEY_PREFIX);

const STEPS = [
  {
    icon: Lightbulb,
    eyebrow: "01 · The methodology",
    title: "11 slides. 5 pillars. Every question funders ask.",
    body: "The Clinq pitch methodology follows what funders actually look at — a real Problem, a Solution that fits, a believable Business Model, credible Traction, a Team that can ship, and a well-structured Ask. Each of the 11 slides answers one funder question. Skip a question, lose the deal.",
    points: [
      "Cover → Problem → Solution → Market → Business Model",
      "Traction → Competitive Landscape → Team",
      "Financial Projections → Capital Structure & Ask → Thanks",
    ],
  },
  {
    icon: PencilSimpleLine,
    eyebrow: "02 · Structured founder inputs",
    title: "Fill in the boxes — we build the slide.",
    body: "Key slides use canonical layouts with dedicated input forms so you don't fight with formatting. Cover has 4 bullets. Market Size has TAM/SAM/SOM + sources. Business Model has 5 fixed rows. Capital Ask has amount, instrument (Equity / Debt / Both) and Use-of-Funds bullets with % allocations. Type — the slide renders live.",
    points: [
      "Locked layouts for Cover, Market, Business Model, Team, Financials, Capital Ask",
      "Field-level AI drafts on each row for a first pass",
      "Live preview mirrors the exported PPTX pixel-for-pixel",
      "Every character checked against the funder failure registry",
    ],
  },
  {
    icon: Shield,
    eyebrow: "03 · Financial Projections + Unit Economics",
    title: "5-year table, plus CAC / LTV / LTV-CAC.",
    body: "Enter revenue, cost of sales and opex per year (up to 6 rows) — gross profit, margin, operating profit and YoY growth are auto-calculated. Below the table, add your CAC and LTV (each tagged Current or Forecast) and the LTV/CAC ratio computes automatically in '5.0x' format, colour-coded green (≥3× strong), amber (≥1× watch) or red (<1× underwater).",
    points: [
      "Auto-calculated GP, margin, op-profit, YoY growth",
      "CAC + LTV with Current/Forecast tags",
      "LTV/CAC ratio with STRONG/WATCH/UNDERWATER status pill",
      "Info tooltips explain every term",
    ],
  },
  {
    icon: PaintBrushBroad,
    eyebrow: "04 · Design gallery (unlocked slides)",
    title: "Change the layout when a slide isn't locked.",
    body: "For slides without a canonical layout (Problem, Solution, Traction, Competitive), pick from 12 pre-built layouts — Photo left, KPI dashboard, Numbered vertical list, Two-column split, Full-bleed hero and more. Sub-slides get their own layout. Reverting to the original is always free.",
    points: [
      "12 layouts across the free-form slides",
      "Sub-slides get their own independent layout choice",
      "'Revert to original · Free' anytime — no lock-in",
      "Locked-layout slides skip the gallery on purpose",
    ],
  },
  {
    icon: ImageIcon,
    eyebrow: "05 · Photos & AI-generated visuals",
    title: "Show, don't just tell.",
    body: "Under every slide there's a Photos & diagrams panel. Upload your own images, or hit Auto-generate and our AI (Gemini Nano Banana) creates an on-topic business photo from your slide's content. Not quite right? Regenerate until it fits.",
    points: [
      "Up to 6 images per slide · PNG/JPG/WEBP/GIF",
      "Auto-generate a photo that matches your slide's content",
      "Regenerate if the first photo isn't quite right",
      "Your uploaded photo always replaces the AI placeholder",
    ],
  },
  {
    icon: StackSimple,
    eyebrow: "06 · Sub-slides for overflow",
    title: "Too much to say? Spill into a sub-slide.",
    body: "When a slide has more content than fits, tap Spill to sub-slide and the overflow bullets move to a new sub-slide. Sub-slides are first-class — they get their own back/next nav, their own layout choice, and even their own photo. Jump between the parent and its sub-slides using the chips right under the content editor.",
    points: [
      "Spill overflow bullets to a new sub-slide",
      "Upload OR AI-regenerate a unique photo per sub-slide",
      "Switch main ↔ sub-slide instantly via jump chips",
      "Sub-slides appear in the exported PPTX AND Play Deck slideshow",
    ],
  },
  {
    icon: PaperPlaneTilt,
    eyebrow: "07 · Ship it",
    title: "Play, share, export — all in one place.",
    body: "Preview + Play Deck + the downloaded PPTX all render from the same engine, so what you see is what your funders get. Hit 'Play Deck' to run a full-screen slideshow (great to rehearse), 'Share' for a one-click read-only public link, or 'Create your pitch deck' to download a fully-editable .pptx with your logo, brand colour and photos baked in.",
    points: [
      "Play Deck: full-screen rehearsal mode, includes sub-slides",
      "Share: one-click read-only public link",
      "Create your pitch deck: fully-editable PPTX with your brand + photos",
      "Upload an existing deck to auto-populate every structured field",
    ],
  },
];

export default function PitchTutorial({ open, onClose, onTalkToAdvisor, deckId }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    if (open) setI(0);
  }, [open]);

  if (!open) return null;

  const step = STEPS[i];
  const Icon = step.icon;
  const isLast = i === STEPS.length - 1;

  const finish = () => {
    try { localStorage.setItem(lsKey(deckId), "1"); } catch { /* ignore */ }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="pitch-tutorial">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={finish}></div>
      <div className="relative w-full max-w-3xl rounded-2xl border overflow-hidden"
        style={{ backgroundColor: "#0A1614", borderColor: "rgba(123, 211, 206, 0.22)" }}>

        {/* Header */}
        <div className="px-7 py-5 border-b flex items-center justify-between"
          style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
          <div className="inline-flex items-center gap-2">
            <BookOpen size={16} weight="fill" style={{ color: C.orange }} />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: C.orange }}>
              How this works · pitch deck builder
            </span>
          </div>
          <button onClick={finish} aria-label="Close" data-testid="pitch-tutorial-close"
            className="text-white/55 hover:text-white p-1 rounded">
            <X size={18} />
          </button>
        </div>

        {/* Progress strip */}
        <div className="flex gap-1.5 px-7 pt-5" data-testid="pitch-tutorial-progress">
          {STEPS.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} data-testid={`pitch-tutorial-dot-${idx}`}
              className="h-1 flex-1 rounded-full transition-colors cursor-pointer"
              style={{ backgroundColor: idx <= i ? C.teal : "rgba(255,255,255,0.08)" }}
              aria-label={`Step ${idx + 1}`} />
          ))}
        </div>

        {/* Body */}
        <div className="px-7 py-7 min-h-[340px]" data-testid={`pitch-tutorial-step-${i}`}>
          <div className="flex items-start gap-5">
            <div className="shrink-0 w-12 h-12 rounded-xl grid place-items-center"
              style={{ backgroundColor: "rgba(10, 139, 133, 0.12)", border: `1px solid ${C.teal}55` }}>
              <Icon size={22} weight="fill" style={{ color: C.tealSoft }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: C.orange }}>{step.eyebrow}</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1.5 text-white"
                style={{ letterSpacing: "-0.025em", lineHeight: 1.15 }}>
                {step.title}
              </h2>
              <p className="text-white/65 text-[13.5px] leading-relaxed mt-3.5">{step.body}</p>
              <ul className="mt-4 space-y-2">
                {step.points.map((p, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-[13px] text-white/80">
                    <Sparkle size={11} weight="fill" className="mt-1 shrink-0" style={{ color: C.tealSoft }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t flex items-center justify-between gap-3"
          style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.25)" }}>
          <button onClick={() => { finish(); onTalkToAdvisor && onTalkToAdvisor(); }}
            data-testid="pitch-tutorial-advisor"
            className="text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors hover:opacity-80"
            style={{ color: C.tealSoft }}>
            <ChatCircleText size={14} weight="fill" /> Or have a 30-min call with a Clinq advisor
          </button>

          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={() => setI(i - 1)} data-testid="pitch-tutorial-prev"
                className="text-xs font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 hover:bg-white/5 transition-colors"
                style={{ color: "rgba(255,255,255,0.65)" }}>
                <ArrowLeft size={13} /> Back
              </button>
            )}
            {!isLast ? (
              <button onClick={() => setI(i + 1)} data-testid="pitch-tutorial-next"
                className="text-xs font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                Next <ArrowRight size={13} />
              </button>
            ) : (
              <button onClick={finish} data-testid="pitch-tutorial-finish"
                className="text-xs font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5"
                style={{ backgroundColor: C.orange, color: "#000" }}>
                <MagicWand size={13} weight="fill" /> Start building
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function shouldShowPitchTutorial(deckId) {
  // Fires the first time the founder lands on the Brand step of THIS deck.
  // Deck-scoped so starting a new pitch always gets a fresh walkthrough,
  // even if the user has seen the tutorial on a previous deck.
  try { return localStorage.getItem(lsKey(deckId)) !== "1"; }
  catch { return true; }
}
